/**
 * SwapCard - Production Token Purchase Component
 *
 * Implements the complete cross-chain swap flow:
 * 1. Connect wallet (EVM via RainbowKit or Solana via wallet adapter)
 * 2. Get/create project-scoped WaaS wallet
 * 3. Ensure USDC ATA exists on Solana
 * 4. Record transaction intent (prepareRelay)
 * 5. Generate EIP-712 permit signature
 * 6. Submit gasless relay transaction
 * 7. Poll status until completion
 *
 * Visual layer: XP grammar (@sw4p/ui). Dark indigo removed.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAccount, useSignTypedData, useSwitchChain } from 'wagmi';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useWallet, useConnection } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { toast } from 'sonner';
import { parseUnits, type Address } from 'viem';
import { PublicKey, VersionedTransaction } from '@solana/web3.js';
import { getAssociatedTokenAddressSync } from '@solana/spl-token';
import bs58 from 'bs58';

import { ButtonCard, StatusLed } from '@sw4p/ui';
import type { LedTone } from '@sw4p/ui';

import { ProjectConfig } from '../hooks/useProject';
import { useAuthToken } from '../hooks/useAuthToken';
import { generatePermitSignature } from '../services/permit';
import { logger } from '../utils/logger';
import TokenPicker from './TokenPicker';
import { buildProjectToken, type TokenInfo } from '../constants/tokens';
import {
    getOrCreateSolanaWallet,
    getProjectWallet,
    ensureUsdcAta,
    prepareRelay,
    getPermitNonce,
    validateNonce,
    relayTransaction,
    getTransactionStatus,
    fetchBridgeFee,
    getJupiterQuote,
    submitCrossChainIntent,
    getIntentStatus,
    getRouteConfig,
    type TransactionStatusValue,
    type RouteConfigResponse,
} from '../services/api';

interface SwapCardProps {
    project: ProjectConfig;
}

// CCTP domain mapping
const CHAIN_TO_DOMAIN: Record<string, number> = {
    eth: 0,
    base: 6,
    arb: 3,
    polygon: 7,
    op: 2,
    avax: 1,
};

const CHAIN_ID_TO_NAME: Record<number, string> = {
    1: 'eth',
    8453: 'base',
    42161: 'arb',
    137: 'polygon',
    10: 'op',
    43114: 'avax',
};

const CHAIN_TO_BACKEND: Record<string, string> = {
    eth: 'ETH',
    base: 'BASE',
    arb: 'ARB',
    polygon: 'MATIC',
    op: 'OP',
    avax: 'AVAX',
    sol: 'SOL',
};

// ZapAndBridge spender addresses (from env or defaults)
const CHAIN_SPENDERS: Record<string, string> = {
    eth: import.meta.env.VITE_ZAP_SPENDER_ETH || '0x0000000000000000000000000000000000000000',
    base: import.meta.env.VITE_ZAP_SPENDER_BASE || '0x3F0c699e2bE8E70F2B7C521F5C5b0fE4c78EEF93',
    arb: import.meta.env.VITE_ZAP_SPENDER_ARB || '0x0000000000000000000000000000000000000000',
    polygon: import.meta.env.VITE_ZAP_SPENDER_MATIC || '0x0000000000000000000000000000000000000000',
};

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';

const USDC_SOLANA_MINT = new PublicKey('EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v');

const toHex32FromBase58 = (address: string): string | null => {
    try {
        const bytes = bs58.decode(address);
        if (bytes.length !== 32) return null;
        const hex = Array.from(bytes).map((b: number) => b.toString(16).padStart(2, '0')).join('');
        return '0x' + hex;
    } catch {
        return null;
    }
};

type SwapStatus = 'idle' | 'preparing' | 'signing' | 'relaying' | 'polling' | 'completed' | 'failed';
type ChainOption = {
    id: string;
    name: string;
    icon: string;
    chainId: number;
    kind: 'evm' | 'sol';
    available: boolean;
    note?: string;
};

const BASE_CHAIN_OPTIONS: Omit<ChainOption, 'available' | 'note'>[] = [
    {
        id: 'base',
        name: 'Base',
        icon: 'https://avatars.githubusercontent.com/u/108554348?s=280&v=4',
        chainId: 8453,
        kind: 'evm',
    },
    {
        id: 'sol',
        name: 'Solana',
        icon: 'https://cryptologos.cc/logos/solana-sol-logo.png?v=026',
        chainId: 0,
        kind: 'sol',
    },
    {
        id: 'eth',
        name: 'Ethereum',
        icon: 'https://cryptologos.cc/logos/ethereum-eth-logo.png?v=026',
        chainId: 1,
        kind: 'evm',
    },
    {
        id: 'polygon',
        name: 'Polygon',
        icon: 'https://cryptologos.cc/logos/polygon-matic-logo.png?v=026',
        chainId: 137,
        kind: 'evm',
    },
    {
        id: 'arb',
        name: 'Arbitrum',
        icon: 'https://cryptologos.cc/logos/arbitrum-arb-logo.png?v=026',
        chainId: 42161,
        kind: 'evm',
    },
    {
        id: 'op',
        name: 'Optimism',
        icon: 'https://cryptologos.cc/logos/optimism-ethereum-op-logo.png?v=026',
        chainId: 10,
        kind: 'evm',
    },
];

const SLIPPAGE_OPTIONS = [50, 100, 200];

// Map swap status to StatusLed tone
function statusToLedTone(status: SwapStatus): LedTone {
    switch (status) {
        case 'completed': return 'live';
        case 'failed':    return 'inactive';
        case 'idle':      return 'inactive';
        default:          return 'building'; // preparing / signing / relaying / polling
    }
}

export default function SwapCard({ project }: SwapCardProps) {
    const [amount, setAmount] = useState('');
    const [chain, setChain] = useState('base');
    const [status, setStatus] = useState<SwapStatus>('idle');
    const [statusMessage, setStatusMessage] = useState('');
    const [jupiterQuote, setJupiterQuote] = useState<string | null>(null);
    const [quoteLoading, setQuoteLoading] = useState(false);
    const [priceImpact, setPriceImpact] = useState<number>(0);
    const [slippageBps, setSlippageBps] = useState(100);
    const [mevProtection, setMevProtection] = useState(false);
    const [mevConfig, setMevConfig] = useState<RouteConfigResponse['mev'] | null>(null);
    const authTokenRef = useRef<string | null>(null);

    // Locked mode: token is fixed to the project token and cannot be changed
    const isLocked = !project.is_universal && project.token_mint;

    const projectToken: TokenInfo = buildProjectToken(project);

    // Token picker state — defaults to project token, user can change in universal mode
    const [selectedToken, setSelectedToken] = useState<TokenInfo>(projectToken);
    const [tokenPickerOpen, setTokenPickerOpen] = useState(false);

    // In locked mode, keep selectedToken pinned to project token (prevent state drift)
    useEffect(() => {
        if (isLocked) {
            setSelectedToken(projectToken);
        }
    }, [isLocked, project.token_mint]);

    // EVM wallet
    const { isConnected: evmConnected, address: evmAddress, chain: connectedChain } = useAccount();
    const { signTypedDataAsync } = useSignTypedData();
    const { switchChain } = useSwitchChain();
    const chainId = connectedChain?.id;

    // Solana wallet
    const { connected: solConnected, publicKey: solPublicKey, signMessage: solSignMessage, sendTransaction } = useWallet();
    const { connection } = useConnection();

    const { getAuthToken } = useAuthToken();

    const hasConfiguredSpender = useCallback((chainKey: string) => {
        const spender = CHAIN_SPENDERS[chainKey];
        return Boolean(spender && spender.toLowerCase() !== ZERO_ADDRESS);
    }, []);

    useEffect(() => {
        let cancelled = false;

        getRouteConfig()
            .then((config) => {
                if (!cancelled) {
                    setMevConfig(config.mev ?? null);
                }
            })
            .catch((error) => {
                logger.warn('[SwapCard]', 'Failed to load MEV route config:', error);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (chain === 'sol' && mevProtection) {
            setMevProtection(false);
        }
    }, [chain, mevProtection]);

    // Fix 7: Wallet disconnect handler — show reconnect message without clearing swap
    useEffect(() => {
        if (!evmConnected && status === 'polling') {
            setStatusMessage('Wallet disconnected. Your swap is still processing. Reconnect to check status.');
        }
    }, [evmConnected, status]);

    const isConnected = evmConnected || solConnected;
    const activeWalletConnected = chain === 'sol' ? solConnected : evmConnected;
    const userAddress = evmAddress || solPublicKey?.toBase58();

    // Fix 1: Resume active swap polling on mount
    useEffect(() => {
        const saved = localStorage.getItem('sw4p_active_swap');
        if (!saved) return;
        try {
            const swap = JSON.parse(saved);
            // Only resume if less than 45 minutes old
            if (Date.now() - swap.timestamp > 45 * 60 * 1000) {
                localStorage.removeItem('sw4p_active_swap');
                return;
            }
            const resumePoll = async () => {
                setStatus('polling');
                setStatusMessage('Resuming swap tracking...');
                const authToken = authTokenRef.current || await requireAuthToken();
                let pollCount = 0;
                const maxPolls = 480;
                const poll = async (): Promise<void> => {
                    if (pollCount >= maxPolls) {
                        localStorage.removeItem('sw4p_active_swap');
                        throw new Error('Transaction timeout');
                    }
                    const statusResp = await getTransactionStatus(swap.txId, authTokenRef.current || authToken);
                    const currentStatus = statusResp.status as TransactionStatusValue;
                    const statusMessages: Record<string, string> = {
                        PENDING: 'Transaction pending...',
                        DETECTED: 'Burn confirmed, waiting for attestation...',
                        ATTESTED: 'Attestation received, minting...',
                        MINTED: 'USDC minted, swapping to token...',
                        SWAPPED: 'Swap complete, finalizing...',
                        COMPLETED: 'Transaction complete!',
                        FAILED: `Transaction failed: ${statusResp.failure_reason || 'Unknown error'}`,
                    };
                    setStatusMessage(statusMessages[currentStatus] || `Status: ${currentStatus}`);
                    if (currentStatus === 'COMPLETED') {
                        setStatus('completed');
                        localStorage.removeItem('sw4p_active_swap');
                        toast.success(`Swap complete!`);
                        return;
                    }
                    if (currentStatus === 'FAILED') {
                        localStorage.removeItem('sw4p_active_swap');
                        throw new Error(statusResp.failure_reason || 'Transaction failed');
                    }
                    pollCount++;
                    await new Promise(r => setTimeout(r, 5000));
                    return poll();
                };
                await poll();
            };
            resumePoll().catch((err) => {
                setStatus('failed');
                setStatusMessage(err.message || 'Failed to resume swap');
                localStorage.removeItem('sw4p_active_swap');
            });
        } catch {
            localStorage.removeItem('sw4p_active_swap');
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const chains: ChainOption[] = BASE_CHAIN_OPTIONS.map((option) => {
        if (option.kind === 'sol') {
            return { ...option, available: true };
        }

        const available = hasConfiguredSpender(option.id);
        return {
            ...option,
            available,
            note: available ? undefined : 'Preview',
        };
    });
    const activeChainOption = chains.find((candidate) => candidate.id === chain) || chains[0];
    const chainMismatch = activeChainOption.kind === 'evm'
        && evmConnected
        && chainId
        && chainId !== activeChainOption.chainId;
    const mevPreferenceAvailable = Boolean(
        mevConfig?.featureEnabled && mevConfig?.requestToggleAvailable && chain !== 'sol'
    );
    const mevToggleDisabled = !mevPreferenceAvailable;
    const plannedProtectedLeg = chain === 'sol'
        ? (mevConfig?.plannedStrategies?.solana || 'NONE')
        : (mevConfig?.plannedStrategies?.evm || 'NONE');
    const mevPreferenceHint = chain === 'sol'
        ? 'Direct Solana swaps on this surface currently use the public Jupiter flow. MEV preference is unavailable here.'
        : !mevConfig
            ? 'Checking MEV protection status...'
            : !mevConfig.featureEnabled
                ? 'Backend MEV protection is currently disabled by feature flag.'
                : !mevConfig.executionIntegrated
                    ? 'Preference only for now. The backend records MEV preference and planned strategy, but private executor wiring is still staged for the live swap path.'
                    : 'Protected private submission is active for this swap surface.';

    // Fetch Jupiter quote when amount changes
    useEffect(() => {
        const fetchQuote = async () => {
            const val = parseFloat(amount) || 0;
            if (val <= 0) {
                setJupiterQuote(null);
                return;
            }

            setQuoteLoading(true);
            try {
                const usdcSmallest = Math.floor(val * 1_000_000);
                const quote = await getJupiterQuote({
                    inputMint: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
                    outputMint: selectedToken.mint,
                    amount: usdcSmallest.toString(),
                    slippageBps,
                });
                setJupiterQuote(quote?.outAmount || null);
                setPriceImpact(quote?.priceImpactPct ? parseFloat(quote.priceImpactPct) : 0);
            } catch {
                setJupiterQuote(null);
                setPriceImpact(0);
            } finally {
                setQuoteLoading(false);
            }
        };

        const debounce = setTimeout(fetchQuote, 500);
        return () => clearTimeout(debounce);
    }, [amount, selectedToken.mint, slippageBps]);

    const handleChainSelect = useCallback((nextChain: ChainOption) => {
        if (!nextChain.available) {
            toast.info(`${nextChain.name} stays preview-only until the storefront path is proven.`);
            return;
        }

        setChain(nextChain.id);

        if (nextChain.kind === 'evm' && evmConnected && chainId !== nextChain.chainId) {
            switchChain({ chainId: nextChain.chainId });
        }
    }, [chainId, evmConnected, switchChain]);

    // Acquire auth token
    const requireAuthToken = useCallback(async (): Promise<string> => {
        if (!userAddress) throw new Error('Wallet not connected');
        const chainType = solConnected ? 'SOL' : 'EVM';
        const token = await getAuthToken({ address: userAddress, chain: chainType });
        authTokenRef.current = token;
        return token;
    }, [userAddress, solConnected, getAuthToken]);

    // Solana native swap (direct Jupiter)
    const handleSolanaSwap = async () => {
        if (!solPublicKey || !solSignMessage || !sendTransaction) {
            toast.error('Solana wallet not connected');
            return;
        }

        try {
            setStatus('preparing');
            setStatusMessage('Getting swap quote...');

            const usdcAmount = Math.floor(parseFloat(amount) * 1_000_000);
            const quote = await getJupiterQuote({
                inputMint: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
                outputMint: selectedToken.mint,
                amount: usdcAmount.toString(),
                slippageBps,
            });

            if (!quote) throw new Error('Failed to get swap quote');

            setStatusMessage('Building transaction...');
            const swapRes = await fetch('https://api.jup.ag/swap/v1/swap', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    quoteResponse: quote,
                    userPublicKey: solPublicKey.toBase58(),
                    wrapAndUnwrapSol: true,
                }),
            });

            if (!swapRes.ok) throw new Error('Failed to build swap transaction');
            const { swapTransaction } = await swapRes.json();

            setStatus('signing');
            setStatusMessage('Please sign the transaction...');
            const txBuf = Buffer.from(swapTransaction, 'base64');
            const transaction = VersionedTransaction.deserialize(txBuf);

            const signature = await sendTransaction(transaction, connection);

            setStatus('polling');
            setStatusMessage('Confirming transaction...');
            const confirm = await connection.confirmTransaction(signature, 'confirmed');

            if (confirm.value.err) {
                throw new Error('Transaction failed');
            }

            setStatus('completed');
            setStatusMessage('Swap complete!');
            toast.success(`Successfully purchased ${selectedToken.symbol}!`);

        } catch (error: any) {
            logger.error('[SwapCard]', 'Solana swap failed:', error);
            setStatus('failed');
            setStatusMessage(error.message || 'Swap failed');
            toast.error(error.message || 'Swap failed');
        }
    };

    // EVM to Solana cross-chain swap
    const handleEvmSwap = async () => {
        if (!evmAddress || !chainId) {
            toast.error('EVM wallet not connected');
            return;
        }

        const currentChainName = CHAIN_ID_TO_NAME[chainId];
        if (!currentChainName) {
            toast.error('Unsupported chain. Please switch to Base or Polygon.');
            return;
        }

        const sourceDomain = CHAIN_TO_DOMAIN[currentChainName];
        if (sourceDomain === undefined) {
            toast.error('Chain not supported for bridging');
            return;
        }

        const spender = CHAIN_SPENDERS[currentChainName];
        if (!spender || spender === '0x0000000000000000000000000000000000000000') {
            toast.error('Bridge not configured for this chain');
            return;
        }

        const destinationDomain = 5; // Solana

        try {
            setStatus('preparing');

            // 1. Get auth token
            setStatusMessage('Authenticating...');
            const authToken = await requireAuthToken();

            // 2. Get/create WaaS wallet for recipient
            setStatusMessage('Initializing recipient wallet...');
            const waasWallet = await getOrCreateSolanaWallet(authToken);

            // 3. Ensure USDC ATA exists
            setStatusMessage('Preparing Solana USDC vault...');
            try {
                const ataResult = await ensureUsdcAta(waasWallet.address, authToken);
                logger.debug('[SwapCard]', 'USDC ATA:', ataResult.ata_address, 'created:', ataResult.created);
            } catch (e) {
                logger.warn('[SwapCard]', 'ATA creation warning (continuing):', e);
            }

            // 4. Derive recipient ATA
            const recipientWallet = new PublicKey(waasWallet.address);
            const recipientATA = getAssociatedTokenAddressSync(USDC_SOLANA_MINT, recipientWallet, true);
            const recipientHex = toHex32FromBase58(recipientATA.toBase58());

            if (!recipientHex) {
                throw new Error('Failed to derive recipient address');
            }

            // 5. Fetch CCTP fee
            setStatusMessage('Calculating bridge fee...');
            const { maxFee, minFinalityThreshold } = await fetchBridgeFee(amount, sourceDomain, destinationDomain);

            // 6. Record intent BEFORE signing
            setStatusMessage('Recording transaction intent...');
            const { intent_id } = await prepareRelay({
                token_in: 'USDC',
                amount_in: amount,
                source_domain: sourceDomain,
                destination_domain: destinationDomain,
                mint_recipient: recipientHex,
                recipient_wallet: waasWallet.address,
                token_out: selectedToken.mint,
            }, authToken);

            // 7. Get permit nonce
            setStatusMessage('Fetching permit nonce...');
            const backendChain = CHAIN_TO_BACKEND[currentChainName];
            const nonceResp = await getPermitNonce(backendChain, evmAddress, authToken);
            const nonceBigInt = BigInt(nonceResp.nonce);

            // 8. Validate nonce is current
            const validation = await validateNonce(backendChain, evmAddress, nonceResp.nonce, authToken);
            if (!validation.valid) {
                throw new Error(`Nonce expired: ${validation.message}`);
            }

            // 9. Generate permit signature
            setStatus('signing');
            setStatusMessage('Please sign the permit in your wallet...');
            const deadline = BigInt(Math.floor(Date.now() / 1000) + 300);

            const permit = await generatePermitSignature(
                evmAddress as Address,
                spender as Address,
                amount,
                chainId,
                nonceBigInt,
                deadline,
                signTypedDataAsync
            );

            // 10. Submit relay transaction
            setStatus('relaying');
            setStatusMessage('Submitting transaction to relay...');

            const idempotencyKey = `${Date.now()}-${evmAddress}-${chainId}-${amount}`;

            // Add 50% buffer to fee, minimum 0.05 USDC
            const baseFee = parseUnits(maxFee, 6);
            const bufferedFee = baseFee > 0n ? (baseFee * 150n / 100n) : 0n;
            const finalFee = bufferedFee < 50000n && bufferedFee > 0n ? 50000n : bufferedFee;

            const relayResponse = await relayTransaction({
                intent_id,
                token_in: 'USDC',
                amount_in: amount,
                source_domain: sourceDomain,
                destination_domain: destinationDomain,
                mint_recipient: recipientHex,
                recipient_wallet: waasWallet.address,
                permit_data: {
                    owner: permit.owner,
                    spender: permit.spender,
                    value: permit.value.toString(),
                    nonce: permit.nonce.toString(),
                    deadline: permit.deadline.toString(),
                    v: permit.v,
                    r: permit.r,
                    s: permit.s,
                },
                idempotency_key: idempotencyKey,
                max_fee: finalFee.toString(),
                min_finality_threshold: minFinalityThreshold,
                token_out: selectedToken.mint,
                mev_protection: mevProtection,
            }, authToken);

            const txId = relayResponse.tx_request_id;
            logger.tx('SUBMITTED', { txId, chain: currentChainName, amount });

            // Save swap context for persistence across page reloads
            localStorage.setItem('sw4p_active_swap', JSON.stringify({
                txId, chain: currentChainName, amount, token: selectedToken.symbol, timestamp: Date.now()
            }));

            // 11. Poll for completion
            setStatus('polling');
            let pollCount = 0;
            const maxPolls = 480; // 40 minutes

            const poll = async (): Promise<void> => {
                if (pollCount >= maxPolls) {
                    throw new Error('Transaction timeout');
                }

                const statusResp = await getTransactionStatus(txId, authTokenRef.current || authToken);
                const currentStatus = statusResp.status as TransactionStatusValue;

                const statusMessages: Record<string, string> = {
                    PENDING: 'Transaction pending...',
                    DETECTED: 'Burn confirmed, waiting for attestation...',
                    ATTESTED: 'Attestation received, minting...',
                    MINTED: 'USDC minted, swapping to token...',
                    SWAPPED: 'Swap complete, finalizing...',
                    COMPLETED: 'Transaction complete!',
                    FAILED: `Transaction failed: ${statusResp.failure_reason || 'Unknown error'}`,
                };

                setStatusMessage(statusMessages[currentStatus] || `Status: ${currentStatus}`);

                if (currentStatus === 'COMPLETED') {
                    setStatus('completed');
                    localStorage.removeItem('sw4p_active_swap');
                    toast.success(`Successfully purchased ${selectedToken.symbol}!`);
                    return;
                }

                if (currentStatus === 'FAILED') {
                    localStorage.removeItem('sw4p_active_swap');
                    throw new Error(statusResp.failure_reason || 'Transaction failed');
                }

                pollCount++;
                await new Promise(r => setTimeout(r, 5000));
                return poll();
            };

            await poll();

        } catch (error: any) {
            logger.error('[SwapCard]', 'EVM swap failed:', error);
            setStatus('failed');
            setStatusMessage(error.message || 'Swap failed');
            localStorage.removeItem('sw4p_active_swap');
            toast.error(error.message || 'Swap failed');
        }
    };

    // Intent-first dual-mode swap: try solver auction, fall back to CCTP relay
    const handleIntentSwap = async () => {
        if (!evmAddress || !chainId) {
            toast.error('EVM wallet not connected');
            return;
        }

        const currentChainName = CHAIN_ID_TO_NAME[chainId];
        if (!currentChainName) {
            await handleEvmSwap();
            return;
        }

        const backendChain = CHAIN_TO_BACKEND[currentChainName];
        if (!backendChain) {
            await handleEvmSwap();
            return;
        }

        try {
            setStatus('preparing');
            setStatusMessage('Finding best price...');

            const authToken = await requireAuthToken();

            // Get/create WaaS wallet for recipient address
            const waasWallet = await getOrCreateSolanaWallet(authToken);

            const usdcAmount = Math.floor(parseFloat(amount) * 1_000_000);
            const deadline = Math.floor(Date.now() / 1000) + 600; // 10 minutes

            const intentQuote = await submitCrossChainIntent({
                source_chain: backendChain,
                dest_chain: 'SOL',
                input_token: 'USDC',
                output_token: selectedToken.mint,
                input_amount: usdcAmount,
                max_slippage_bps: slippageBps,
                mev_protection: mevProtection,
                deadline,
                recipient: waasWallet.address,
                sender: evmAddress,
            }, authToken);

            const intentId = intentQuote.intentId;
            logger.debug('[SwapCard]', 'Intent submitted:', intentId);

            // Poll for solver fill for up to 30 seconds (15 polls x 2s)
            const SOLVER_TIMEOUT_MS = 30_000;
            const POLL_INTERVAL_MS = 2_000;
            const maxPolls = Math.floor(SOLVER_TIMEOUT_MS / POLL_INTERVAL_MS);
            let solverFilled = false;

            for (let i = 0; i < maxPolls; i++) {
                await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));

                try {
                    const statusResp = await getIntentStatus(intentId, authTokenRef.current || authToken);
                    const state = statusResp.state;

                    if (state === 'FILLED' || state === 'SETTLED' || state === 'COMPLETED') {
                        solverFilled = true;
                        setStatusMessage('Solver found! Delivering...');
                        logger.tx('INTENT_FILLED', { intentId, fillTx: statusResp.fillTxHash });

                        // If already completed, we're done
                        if (state === 'COMPLETED') {
                            setStatus('completed');
                            setStatusMessage('Transaction complete!');
                            toast.success(`Successfully purchased ${selectedToken.symbol}!`);
                            return;
                        }

                        // Continue polling until COMPLETED
                        setStatus('polling');
                        const completionMaxPolls = 120; // up to 4 more minutes
                        for (let j = 0; j < completionMaxPolls; j++) {
                            await new Promise(r => setTimeout(r, 2_000));
                            const finalStatus = await getIntentStatus(intentId, authTokenRef.current || authToken);
                            if (finalStatus.state === 'COMPLETED') {
                                setStatus('completed');
                                setStatusMessage('Transaction complete!');
                                toast.success(`Successfully purchased ${selectedToken.symbol}!`);
                                return;
                            }
                            if (finalStatus.state === 'FAILED') {
                                throw new Error('Intent fill failed');
                            }
                            setStatusMessage('Solver found! Delivering...');
                        }
                        // If we got here, it's still not completed but was filled
                        setStatus('completed');
                        setStatusMessage('Transaction submitted! Check your wallet shortly.');
                        toast.success(`${selectedToken.symbol} purchase submitted!`);
                        return;
                    }

                    if (state === 'FAILED' || state === 'CANCELLED' || state === 'EXPIRED') {
                        logger.debug('[SwapCard]', `Intent ${state}, falling back to relay`);
                        break;
                    }

                    // Still waiting — show countdown
                    const remaining = Math.max(0, maxPolls - i - 1) * 2;
                    setStatusMessage(`Finding best price... (${remaining}s)`);
                } catch (pollErr) {
                    logger.warn('[SwapCard]', 'Intent poll error (continuing):', pollErr);
                }
            }

            if (!solverFilled) {
                // No solver filled within 30s — fall back to standard CCTP relay
                logger.debug('[SwapCard]', 'No solver fill, falling back to CCTP relay');
                setStatusMessage('Using bridge (~60s)...');
                setStatus('idle'); // Reset so handleEvmSwap can set its own states
                await handleEvmSwap();
            }

        } catch (error: any) {
            // If intent submission itself fails (e.g. endpoint doesn't exist),
            // gracefully fall back to existing relay flow
            if (error.message?.includes('Failed to submit intent') ||
                error.message?.includes('404') ||
                error.message?.includes('Not Found')) {
                logger.debug('[SwapCard]', 'Intent endpoint unavailable, using relay:', error.message);
                setStatus('idle');
                await handleEvmSwap();
                return;
            }

            logger.error('[SwapCard]', 'Intent swap failed:', error);
            setStatus('failed');
            setStatusMessage(error.message || 'Swap failed');
            toast.error(error.message || 'Swap failed');
        }
    };

    const handleBuy = async () => {
        if (!activeWalletConnected) {
            toast.error(chain === 'sol' ? 'Please connect a Solana wallet' : 'Please connect an EVM wallet');
            return;
        }

        if (!activeChainOption.available) {
            toast.error(`${activeChainOption.name} is still preview-only in the storefront.`);
            return;
        }

        if (chainMismatch) {
            switchChain({ chainId: activeChainOption.chainId });
            return;
        }

        const amountNum = parseFloat(amount);
        if (isNaN(amountNum) || amountNum < 5.55) {
            toast.error('Minimum amount is $5.55 USDC');
            return;
        }

        if (chain === 'sol') {
            await handleSolanaSwap();
        } else {
            // Dual-mode: try intent/solver first, fall back to CCTP relay
            await handleIntentSwap();
        }
    };

    const handleReset = () => {
        setStatus('idle');
        setStatusMessage('');
        setAmount('');
        setJupiterQuote(null);
        setPriceImpact(0);
        localStorage.removeItem('sw4p_active_swap');
    };

    const getQuoteDisplay = () => {
        if (quoteLoading) return 'Calculating...';
        if (jupiterQuote) {
            const tokens = parseFloat(jupiterQuote) / Math.pow(10, selectedToken.decimals);
            return tokens.toLocaleString(undefined, { maximumFractionDigits: 0 });
        }
        return '--';
    };

    const isBusy = status !== 'idle' && status !== 'completed' && status !== 'failed';

    const xpPad: React.CSSProperties = { padding: '16px 20px' };
    const xpSection: React.CSSProperties = { marginBottom: 14 };

    return (
        <div style={{ fontFamily: 'var(--sw4p-font-body)' }}>
            {/* Token Picker Modal (universal mode only) */}
            {!isLocked && (
                <TokenPicker
                    isOpen={tokenPickerOpen}
                    onClose={() => setTokenPickerOpen(false)}
                    onSelect={(token) => {
                        setSelectedToken(token);
                        setJupiterQuote(null);
                    }}
                    currentMint={selectedToken.mint}
                    projectToken={projectToken}
                />
            )}

            <div style={xpPad}>
                {/* Header */}
                <div style={{ ...xpSection, display: 'flex', alignItems: 'center', gap: 10 }}>
                    {isLocked ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                            {selectedToken.logoUri ? (
                                <img src={selectedToken.logoUri} alt={selectedToken.symbol} style={{ width: 32, height: 32, borderRadius: '50%' }} />
                            ) : (
                                <div style={{
                                    width: 32, height: 32, borderRadius: '50%',
                                    background: 'var(--tenant-accent, var(--xp-titlebar-flat))',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: '#fff', fontWeight: 700, fontSize: 14,
                                }}>
                                    {selectedToken.symbol.charAt(0)}
                                </div>
                            )}
                            <div>
                                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--xp-text-ink)' }}>
                                    Buy {selectedToken.symbol}
                                </div>
                                <div style={{ fontSize: 11, color: 'var(--xp-text-gray)' }}>{selectedToken.name}</div>
                            </div>
                        </div>
                    ) : (
                        <button
                            onClick={() => setTokenPickerOpen(true)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 10, flex: 1,
                                background: 'none', border: 'none', cursor: 'pointer',
                                padding: '4px 6px', borderRadius: 3,
                                fontFamily: 'var(--sw4p-font-body)',
                            }}
                            onMouseEnter={e => (e.currentTarget.style.background = '#EDF4FE')}
                            onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                        >
                            {selectedToken.logoUri ? (
                                <img src={selectedToken.logoUri} alt={selectedToken.symbol} style={{ width: 32, height: 32, borderRadius: '50%' }} />
                            ) : (
                                <div style={{
                                    width: 32, height: 32, borderRadius: '50%',
                                    background: 'var(--tenant-accent, var(--xp-titlebar-flat))',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: '#fff', fontWeight: 700, fontSize: 14,
                                }}>
                                    {selectedToken.symbol.charAt(0)}
                                </div>
                            )}
                            <div style={{ textAlign: 'left' }}>
                                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--xp-text-ink)', display: 'flex', alignItems: 'center', gap: 6 }}>
                                    Buy Any Token <span style={{ fontSize: 10, color: 'var(--xp-text-gray)' }}>v</span>
                                </div>
                                <div style={{ fontSize: 11, color: 'var(--xp-text-gray)' }}>{selectedToken.name}</div>
                            </div>
                        </button>
                    )}

                    {project.is_verified && selectedToken.mint === project.token_mint && (
                        <span style={{
                            marginLeft: 'auto',
                            background: '#C1F0B7', color: '#1B4D04',
                            border: '1px solid #3C8D0D',
                            borderRadius: 2, padding: '2px 8px', fontSize: 11, fontWeight: 700,
                        }}>
                            Verified
                        </span>
                    )}
                </div>

                {/* Status display */}
                {status !== 'idle' && (
                    <div style={xpSection}>
                        <div className={`xp-status-row${status === 'completed' ? ' xp-status-row--success' : status === 'failed' ? ' xp-status-row--error' : ''}`}>
                            <StatusLed
                                tone={statusToLedTone(status)}
                                size="sm"
                            />
                            {isBusy && <span className="xp-spinner" style={{ width: 12, height: 12 }} />}
                            <span style={{ fontSize: 12, flex: 1 }}>{statusMessage}</span>
                        </div>
                    </div>
                )}

                {(status === 'completed' || status === 'failed') && (
                    <div style={{ ...xpSection }}>
                        <ButtonCard
                            onClick={handleReset}
                            variant="default"
                            style={{ width: '100%', justifyContent: 'center' } as React.CSSProperties}
                        >
                            {status === 'completed' ? 'Buy More' : 'Try Again'}
                        </ButtonCard>
                    </div>
                )}

                {status === 'idle' && (
                    <>
                        {/* Chain selector */}
                        <div style={xpSection}>
                            <label className="xp-label">Pay from</label>
                            <div className="grid grid-cols-3 gap-2" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                                {chains.map((c) => (
                                    <button
                                        key={c.id}
                                        onClick={() => handleChainSelect(c)}
                                        disabled={!c.available}
                                        className={`xp-chain-chip${chain === c.id ? ' xp-chain-chip--selected' : ''}${!c.available ? ' xp-chain-chip--unavailable' : ''}`}
                                    >
                                        <img src={c.icon} alt={c.name} style={{ width: 28, height: 28 }} />
                                        <span style={{ fontSize: 11 }}>{c.name}</span>
                                        {c.note && (
                                            <span style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--xp-text-gray)' }}>
                                                {c.note}
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>
                            {chainMismatch && (
                                <p style={{ marginTop: 6, fontSize: 11, color: '#7a5800' }}>
                                    Switch your wallet to {activeChainOption.name} before buying.
                                </p>
                            )}
                            {!activeChainOption.available && (
                                <p style={{ marginTop: 6, fontSize: 11, color: 'var(--xp-text-gray)' }}>
                                    {activeChainOption.name} is shown for roadmap visibility only. The live storefront flow is currently Base or Solana.
                                </p>
                            )}
                        </div>

                        {/* Amount input */}
                        <div style={xpSection}>
                            <label className="xp-label">Amount (USDC)</label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type="number"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    placeholder="0.00"
                                    min="5.55"
                                    step="0.01"
                                    className="xp-input"
                                    style={{ paddingRight: 56 }}
                                />
                                <span style={{
                                    position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                                    fontSize: 12, color: 'var(--xp-text-gray)',
                                }}>USDC</span>
                            </div>
                            {amount && parseFloat(amount) > 0 && parseFloat(amount) < 5.55 ? (
                                <p style={{ fontSize: 11, color: '#b81c1c', marginTop: 3 }}>Minimum $5.55</p>
                            ) : (
                                <p style={{ fontSize: 11, color: 'var(--xp-text-gray)', marginTop: 3 }}>Minimum: $5.55 USDC</p>
                            )}
                        </div>

                        {/* Quote panel */}
                        {parseFloat(amount) >= 5.55 && (
                            <div style={{
                                ...xpSection,
                                background: '#EDF4FE',
                                border: '1px solid var(--xp-border-card)',
                                borderRadius: 2,
                                padding: '10px 12px',
                            }}>
                                {/* Slippage */}
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>
                                    <span style={{ fontSize: 12, color: 'var(--xp-text-gray)' }}>Slippage tolerance</span>
                                    <div style={{ display: 'flex', gap: 4 }}>
                                        {SLIPPAGE_OPTIONS.map((option) => (
                                            <button
                                                key={option}
                                                type="button"
                                                onClick={() => setSlippageBps(option)}
                                                style={{
                                                    fontFamily: 'var(--sw4p-font-body)',
                                                    fontSize: 11,
                                                    padding: '2px 8px',
                                                    borderRadius: 2,
                                                    border: slippageBps === option ? '1px solid var(--tenant-accent, var(--xp-titlebar-flat))' : '1px solid var(--xp-border-card)',
                                                    background: slippageBps === option ? 'color-mix(in srgb, var(--tenant-accent, #0046A4) 15%, #fff)' : '#fff',
                                                    color: slippageBps === option ? 'var(--xp-text-ink)' : 'var(--xp-text-gray)',
                                                    cursor: 'pointer',
                                                    fontWeight: slippageBps === option ? 700 : 400,
                                                }}
                                            >
                                                {(option / 100).toFixed(option % 100 === 0 ? 0 : 1)}%
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                                    <span style={{ color: 'var(--xp-text-gray)' }}>You receive (est.)</span>
                                    <span style={{ fontWeight: 700, color: 'var(--xp-text-ink)' }}>
                                        {getQuoteDisplay()} {selectedToken.symbol}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                                    <span style={{ color: 'var(--xp-text-gray)' }}>Fee</span>
                                    <span style={{ color: 'var(--xp-text-muted)' }}>~1%</span>
                                </div>
                                {chain !== 'sol' && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                                        <span style={{ color: 'var(--xp-text-gray)' }}>Estimated delivery</span>
                                        <span style={{ color: 'var(--xp-text-muted)' }}>~5s via solver, ~60s via CCTP</span>
                                    </div>
                                )}
                                {chain !== 'sol' && (
                                    <p style={{ fontSize: 10, color: 'var(--xp-text-gray)', marginTop: 4, lineHeight: 1.4 }}>
                                        Slippage applies to the quote and intent route. If the flow falls back to the bridge path, settlement continues under backend swap defaults.
                                    </p>
                                )}
                                {/* Price impact warning */}
                                {priceImpact >= 1 && (
                                    <div style={{
                                        marginTop: 6, padding: '4px 8px', borderRadius: 2, fontSize: 12,
                                        background: priceImpact > 5 ? '#fff0f0' : '#FFEAB3',
                                        border: `1px solid ${priceImpact > 5 ? '#b81c1c' : '#E5A100'}`,
                                        color: priceImpact > 5 ? '#7a1010' : '#5C3A00',
                                        display: 'flex', gap: 6, alignItems: 'center',
                                    }}>
                                        {priceImpact > 5 && <span>!</span>}
                                        <span>
                                            {priceImpact > 10
                                                ? 'Price impact too high'
                                                : priceImpact > 5
                                                    ? `High price impact: ${priceImpact.toFixed(1)}%. Reduce order size or choose a deeper market.`
                                                    : `Price impact: ${priceImpact.toFixed(1)}%`}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* MEV preference */}
                        <div style={{
                            ...xpSection,
                            background: '#FAFBFC',
                            border: '1px solid var(--xp-border-card)',
                            borderRadius: 2,
                            padding: '8px 12px',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
                                <div>
                                    <div style={{ fontSize: 12, color: 'var(--xp-text-ink)', fontWeight: 600 }}>MEV protection preference</div>
                                    <div style={{ fontSize: 10, color: 'var(--xp-text-gray)' }}>
                                        Planned protected leg: {plannedProtectedLeg}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (!mevToggleDisabled) {
                                            setMevProtection((current) => !current);
                                        }
                                    }}
                                    disabled={mevToggleDisabled}
                                    style={{
                                        fontFamily: 'var(--sw4p-font-body)',
                                        fontSize: 11,
                                        padding: '3px 10px',
                                        borderRadius: 2,
                                        border: mevProtection ? '1px solid #3C8D0D' : '1px solid var(--xp-border-card)',
                                        background: mevProtection ? '#C1F0B7' : '#fff',
                                        color: mevProtection ? '#1B4D04' : 'var(--xp-text-gray)',
                                        cursor: mevToggleDisabled ? 'not-allowed' : 'pointer',
                                        opacity: mevToggleDisabled ? 0.55 : 1,
                                        fontWeight: 600,
                                    }}
                                >
                                    {mevProtection ? 'Requested' : 'Standard'}
                                </button>
                            </div>
                            <p style={{ fontSize: 11, color: 'var(--xp-text-gray)', lineHeight: 1.5 }}>
                                {mevPreferenceHint}
                            </p>
                        </div>

                        {/* Connect / Buy button */}
                        {!activeWalletConnected ? (
                            <div>
                                {chain === 'sol' ? (
                                    <WalletMultiButton className="!w-full !justify-center !rounded-none !py-3" />
                                ) : (
                                    <ConnectButton.Custom>
                                        {({ openConnectModal }) => (
                                            <ButtonCard
                                                variant="primary"
                                                onClick={openConnectModal}
                                                style={{ width: '100%', justifyContent: 'center' } as React.CSSProperties}
                                            >
                                                Connect Wallet
                                            </ButtonCard>
                                        )}
                                    </ConnectButton.Custom>
                                )}
                            </div>
                        ) : (
                            <ButtonCard
                                variant="primary"
                                onClick={handleBuy}
                                disabled={
                                    isBusy ||
                                    !amount ||
                                    parseFloat(amount) < 5.55 ||
                                    priceImpact > 10 ||
                                    !activeChainOption.available
                                }
                                style={{ width: '100%', justifyContent: 'center' } as React.CSSProperties}
                            >
                                {chainMismatch
                                    ? `Switch to ${activeChainOption.name}`
                                    : priceImpact > 10
                                        ? 'Price impact too high'
                                        : !activeChainOption.available
                                            ? `${activeChainOption.name} preview only`
                                            : `Buy ${selectedToken.symbol}`}
                            </ButtonCard>
                        )}
                    </>
                )}

                {/* Footer */}
                <p style={{ textAlign: 'center', fontSize: 11, color: 'var(--xp-text-gray)', marginTop: 14 }}>
                    Powered by <a href="https://sw4p.io" style={{ color: 'var(--xp-titlebar-flat)', textDecoration: 'underline' }}>SW4P</a>
                </p>
            </div>
        </div>
    );
}
